import React from "react";
import { CatalogItem } from "../../api/CataloguesAPI";
import DressCard from "../DressCard/DressCard";
import noImage from "../../assets/images/null.png";
import { FixedSizeGrid as Grid, GridChildComponentProps } from "react-window";
import AutoSizer from "react-virtualized-auto-sizer";

type DressGridProps<T extends CatalogItem> = {
  items: T[];
  onCardClick?: (item: T) => void;
  /** Minimum width each card should have (in px) before wrapping to a new column. */
  minCardWidth?: number;
  /** Fixed height for each row/cell (in px) on larger screens. */
  cardHeight?: number;
  /** Fixed height for each row/cell (in px) on mobile screens. */
  mobileCardHeight?: number;
};

export default function DressGrid<T extends CatalogItem>({
  items,
  onCardClick = () => null,
  minCardWidth = 300,
  cardHeight = 650,
  mobileCardHeight = 300
}: DressGridProps<T>) {
  // Type passed to each Grid cell
  type GridData = {
    items: T[];
    onCardClick: (item: T) => void;
    columnCount: number;
  };

  return (
    <div style={{ width: "100%", height: "90vh" }}>
      <AutoSizer>
        {({ width, height }: { width: number; height: number }) => {
          let columnCount = 0;
          let rowHeight = cardHeight;

          // If container is narrower than 600px, force 2 columns and smaller row height
          if (width < 600) {
            columnCount = 2;
            rowHeight = mobileCardHeight;
          } else {
            // Otherwise, use our original math
            columnCount = Math.max(1, Math.floor(width / minCardWidth));
          }

          const rowCount = Math.ceil(items.length / columnCount);
          const columnWidth = width / columnCount;

          const data: GridData = {
            items,
            onCardClick,
            columnCount
          };

          return (
            <Grid<GridData>
              columnCount={columnCount}
              rowCount={rowCount}
              columnWidth={columnWidth}
              rowHeight={rowHeight}
              width={width}
              height={height}
              itemData={data}
            >
              {({ columnIndex, rowIndex, style, data }: GridChildComponentProps<GridData>) => {
                const { items, onCardClick, columnCount } = data;
                const itemIndex = rowIndex * columnCount + columnIndex;
                if (itemIndex >= items.length) return null;

                const item = items[itemIndex];
                return (
                  <div style={style}>
                    <div onClick={() => onCardClick(item)} className="px-5 py-[10px]">
                      <DressCard
                        imageUrl={item?.media?.[0] || noImage}
                        name={item?.name}
                      />
                    </div>
                  </div>
                );
              }}
            </Grid>
          );
        }}
      </AutoSizer>
    </div>
  );
}